// ShareBite search & filter evaluation (Task 9.1).
//
// Pure, platform-independent evaluation of a search-and-filter request over a
// collection of items (Donations for NGOs, NGOs for Restaurants). The result
// set contains exactly the items whose searchable text contains every query
// term AND that satisfy every selected filter — filters combine conjunctively
// (Req 22.1, 22.2, 22.3, 22.4). A query of zero characters is rejected with a
// "search term is required" indication (Req 22.6).
//
// The function performs no I/O and has no side effects; the caller supplies the
// items, the raw query string, an accessor for each item's searchable text, and
// the set of currently-selected filter predicates.
//
// Design reference: design.md "Property 19: Search and filter result
// correctness".
// Requirements: 22.1, 22.2, 22.3, 22.4, 22.6.
/** Error code returned for an empty (zero-character) query (Req 22.6). */
export const SEARCH_TERM_REQUIRED = "search term is required";
/**
 * Split a query into individual terms on whitespace, dropping empty fragments.
 * Comparison elsewhere is case-insensitive, so terms are lower-cased here.
 */
function queryTerms(query) {
    return query
        .toLowerCase()
        .split(/\s+/)
        .filter((term) => term.length > 0);
}
/**
 * Whether an item's searchable text contains every query term (case-insensitive
 * substring match). An empty term list (a non-empty but whitespace-only query)
 * matches every item.
 */
function matchesQuery(searchText, terms) {
    const haystack = searchText.toLowerCase();
    return terms.every((term) => haystack.includes(term));
}
/**
 * Evaluate a search-and-filter request (Req 22.1–22.4, 22.6).
 *
 * If `query` has zero characters the request is rejected with
 * {@link SEARCH_TERM_REQUIRED} and no results (Req 22.6). Otherwise the result
 * set contains exactly the items for which BOTH hold:
 *  - the item's searchable text contains every query term (case-insensitive
 *    substring match), AND
 *  - every selected filter returns `true` for the item (conjunctive).
 *
 * Input order is preserved. This function is pure: it reads only its argument,
 * mutates nothing, and produces no side effects.
 *
 * @param input The {@link SearchInput} describing the request.
 * @returns A {@link SearchResult}.
 */
export function evaluateSearch(input) {
    if (input.query.length === 0) {
        return { accepted: false, error: SEARCH_TERM_REQUIRED };
    }
    const terms = queryTerms(input.query);
    const filters = input.filters ?? [];
    const results = input.items.filter((item) => matchesQuery(input.getSearchText(item), terms) &&
        filters.every((filter) => filter(item)));
    return { accepted: true, results };
}
//# sourceMappingURL=search.js.map